import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
} from '@mui/material';
import { Cancel, Edit, InfoOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import UserSelectModal from './UserSearchModal';
import DateFilter from './DateFilter';
import ConfirmationDialog from './ConfirmationDialog';
import MessageCreate from '../Pages/Message/MessageCreate';
import { getLCRoleHistory, RoleHistoryResponse } from '../endpoints/chaptersTs';

interface UserObject {
  id: string;
  full_name: string;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface PositionDisplayProps {
  id: string;
  position: string;
  positionId?: string;
  committeeId?: string;
  canEdit: boolean;
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  isSaving: boolean;
  errorSavingText?: string;
  onSave: (data: SaveData) => void;
  userId?: string;
  pageFrom?: string;
  localChapterId?: string | null;
  spdChapterId?: string | null;
  canMessage?: boolean;
  showRoleHistory?: boolean;
}

export default function PositionDisplay({
  id,
  position,
  positionId,
  committeeId,
  canEdit,
  name,
  startDate,
  endDate,
  isSaving,
  errorSavingText,
  onSave,
  userId,
  pageFrom,
  localChapterId = null,
  spdChapterId = null,
  canMessage = true,
  showRoleHistory = false,
}: PositionDisplayProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editDates, setEditDates] = useState<boolean>(false);
  const [newAppointment, setNewAppointment] = useState<boolean>(false);
  const [removeRecord, setRemoveRecord] = useState<boolean>(false);

  const [startDateValue, setStartDateValue] = useState<string | null>(
    startDate
  );
  const [endDateValue, setEndDateValue] = useState<string | null>(endDate);
  // For TLC: when in editDates mode, allow changing Primary vs Contact
  const [positionIdValue, setPositionIdValue] = useState<string | undefined>(
    positionId
  );

  const [userIdValue, setUserIdValue] = useState<string | null>(null);
  const [userNameValue, setUserNameValue] = useState<string | null>(name);
  const [showUserSelect, setShowUserSelect] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [sendingSave, setSendingSave] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [roleHistoryAnchor, setRoleHistoryAnchor] =
    useState<HTMLElement | null>(null);
  const [roleHistoryData, setRoleHistoryData] =
    useState<RoleHistoryResponse | null>(null);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState<boolean>(false);
  const [roleHistoryError, setRoleHistoryError] = useState<string | null>(null);

  const handleRoleHistoryClick = async (
    event: React.MouseEvent<HTMLElement>
  ): Promise<void> => {
    if (!localChapterId || !positionId) return;
    setRoleHistoryAnchor(event.currentTarget);
    setRoleHistoryData(null);
    setRoleHistoryError(null);
    setRoleHistoryLoading(true);
    try {
      const data = await getLCRoleHistory(localChapterId, positionId);
      setRoleHistoryData(data);
    } catch (err: unknown) {
      setRoleHistoryError(
        err instanceof Error ? err.message : 'Failed to load role history'
      );
    } finally {
      setRoleHistoryLoading(false);
    }
  };

  const handleRoleHistoryClose = (): void => {
    setRoleHistoryAnchor(null);
  };

  const resetState = useCallback((): void => {
    setIsEditing(false);
    setEditDates(false);
    setNewAppointment(false);
    setRemoveRecord(false);
    setStartDateValue(null);
    setEndDateValue(null);
    setPositionIdValue(positionId);
    setUserIdValue(null);
    setUserNameValue(null);
    setShowUserSelect(false);
  }, [positionId]);

  useEffect(() => {
    if (!isSaving && sendingSave && !errorSavingText) {
      setSendingSave(false);
      resetState();
    }
    if (errorSavingText) {
      console.log('Error saving In PD:', errorSavingText);
      setSendingSave(false);
      setErrorMessage(errorSavingText);
    }
  }, [sendingSave, errorSavingText, isSaving, resetState]);

  const handleEditClick = (): void => {
    if (isEditing) {
      resetState();
    } else {
      setIsEditing(true);
    }
  };

  const showButtons = (): boolean => {
    if (isEditing && !editDates && !newAppointment && !removeRecord) {
      return true;
    } else {
      return false;
    }
  };

  const handleSetNewAppointment = (): void => {
    setNewAppointment(true);
    setEditDates(false);
    setRemoveRecord(false);
    setShowUserSelect(true);
  };

  const handleSetEditDates = (): void => {
    setEditDates(true);
    setNewAppointment(false);
    setRemoveRecord(false);
    setUserNameValue(name);
    setStartDateValue(startDate);
    setEndDateValue(endDate);
    setPositionIdValue(positionId ?? 'Contact');
  };

  const handleSetRemoveRecord = (): void => {
    setRemoveRecord(true);
    setEditDates(false);
    setNewAppointment(false);
  };

  const handleUserSelect = (userObject: UserObject): void => {
    let userId = userObject.id;
    let userName = userObject.full_name;
    setUserIdValue(userId);
    setUserNameValue(userName);
    setShowUserSelect(false);
  };

  const handleConfirmRemove = (): void => {
    setSendingSave(true);
    setRemoveRecord(false);
    onSave({
      id: id,
      committeeId: committeeId,
      action: 'removeRecord',
    });
  };

  const handleSave = (): void => {
    // Validate the input values
    if (newAppointment) {
      if (!userIdValue) {
        setErrorMessage('Please select a user');
        return;
      }
      if (!startDateValue) {
        setErrorMessage('Please select a start date');
        return;
      }
    }
    if (editDates) {
      if (!startDateValue) {
        setErrorMessage('Please select a start date');
        return;
      }
    }
    // Call onSave function with the new values and action (Date or New Appointment)
    setSendingSave(true);
    if (newAppointment) {
      onSave({
        id: id,
        positionId: positionId,
        committeeId: committeeId,
        action: 'newAppointment',
        userId: userIdValue,
        startDate: startDateValue,
        endDate: endDateValue,
      });
    } else if (editDates) {
      const payload: SaveData = {
        id: id,
        committeeId: committeeId,
        action: 'editDates',
        startDate: startDateValue,
        endDate: endDateValue,
      };
      if (pageFrom === 'tlc' && positionIdValue !== undefined) {
        payload.positionId = positionIdValue;
      }
      onSave(payload);
    }
  };

  const sendMessage = (): void => {
    setModalOpen(true);
  };

  return (
    <Grid container item xs={12} key={id}>
      {showButtons() && (
        <Grid item xs={12} sx={{ padding: 1 }}>
          <Divider sx={{ marginBottom: 1 }} />
          <ButtonGroup
            variant="contained"
            aria-label="text button group"
            size="small"
            fullWidth
          >
            <Button
              onClick={() => {
                handleSetNewAppointment();
              }}
              color="success"
              sx={{ fontWeight: 'bold', padding: 0, pt: 1, pb: 1 }}
            >
              {`Appoint New`}
            </Button>
            {name && (
              <Button
                onClick={() => {
                  handleSetEditDates();
                }}
                color="primary"
                sx={{ fontWeight: 'bold', padding: 0, pt: 1, pb: 1 }}
              >
                {`Update Dates`}
              </Button>
            )}
            {name && (
              <Button
                onClick={() => {
                  handleSetRemoveRecord();
                }}
                sx={{
                  fontWeight: 'bold',
                  padding: 0,
                  pt: 1,
                  pb: 1,
                  background: '#0072CE',
                }}
              >
                {`Undo Record`}
              </Button>
            )}
          </ButtonGroup>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        flex={1}
        flexDirection={'row'}
        justifyContent={'space-between'}
        display={'flex'}
      >
        <Typography variant="body1" fontWeight={'bold'}>
          {position}
          {showRoleHistory && positionId && localChapterId && (
            <IconButton
              onClick={handleRoleHistoryClick}
              sx={{ padding: 0, marginLeft: 0.5 }}
              size="small"
              aria-label="View role history"
            >
              <InfoOutlined
                fontSize="small"
                sx={{ fontSize: 18 }}
                color="action"
              />
            </IconButton>
          )}
          {canEdit && !isEditing && (
            <IconButton
              onClick={() => {
                handleEditClick();
              }}
              sx={{ padding: 0, marginLeft: 1 }}
            >
              <Edit fontSize="small" color="primary" sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {isEditing && (
            <IconButton
              onClick={() => {
                handleEditClick();
              }}
              sx={{ padding: 0, marginLeft: 1 }}
            >
              <Cancel fontSize="small" color="primary" sx={{ fontSize: 25 }} />
            </IconButton>
          )}
        </Typography>
        {!isEditing && userId && canMessage && (
          <Button
            onClick={() => {
              sendMessage();
            }}
            variant="contained"
            size={'small'}
            sx={{ fontWeight: 'bold', pb: 0, pt: 0, pl: 1, pr: 1 }}
          >
            {userId === localStorage.getItem('user_id')
              ? 'Message Myself'
              : `Message`}
          </Button>
        )}
      </Grid>
      {!newAppointment && (
        <Grid item xs={12}>
          {name ? (
            <Typography variant="body1">{name}</Typography>
          ) : (
            <Typography variant="body1">Vacant</Typography>
          )}
        </Grid>
      )}
      {!newAppointment && !editDates ? (
        <Grid container item xs={12}>
          {startDate ? (
            <Grid item xs={12}>
              <Typography variant="caption" fontSize={12} paddingBottom={1}>
                {dayjs(startDate).format('MM/DD/YYYY')} -
              </Typography>
              <Typography
                variant="caption"
                fontSize={12}
                paddingBottom={1}
                paddingLeft={1}
              >
                {endDate ? dayjs(endDate).format('MM/DD/YYYY') : 'Present'}
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="caption" fontSize={12}>
                N/A
              </Typography>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container item xs={12}>
          <Divider sx={{ marginBottom: 1 }} />
          <Grid item xs={12}>
            {newAppointment && (
              <Typography variant="body1">
                {userNameValue ? userNameValue : 'Please select a user'}
                <IconButton
                  onClick={() => {
                    setShowUserSelect(true);
                  }}
                  sx={{ padding: 0, marginLeft: 1 }}
                >
                  <Edit
                    fontSize="small"
                    color="primary"
                    sx={{ fontSize: 18 }}
                  />
                </IconButton>
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sx={{ padding: 1 }}>
            <DateFilter
              filterValue={startDateValue}
              updateFilters={setStartDateValue}
              label={'Start Date'}
              disabled={false}
            />
          </Grid>
          {editDates && (
            <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
              <DateFilter
                filterValue={endDateValue}
                updateFilters={setEndDateValue}
                label={'End Date'}
                disabled={false}
              />
            </Grid>
          )}
          {pageFrom === 'tlc' && editDates && (
            <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="tlc-position-label">Role</InputLabel>
                <Select
                  labelId="tlc-position-label"
                  value={positionIdValue ?? 'Contact'}
                  label="Role"
                  onChange={(e) =>
                    setPositionIdValue(e.target.value as 'Primary' | 'Contact')
                  }
                >
                  <MenuItem value="Primary">Primary</MenuItem>
                  <MenuItem value="Contact">Contact</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sx={{ paddingBottom: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                handleSave();
              }}
              disabled={sendingSave}
              sx={{ fontWeight: 'bold' }}
            >
              Save
            </Button>
          </Grid>
          {errorMessage && (
            <Grid item xs={12} sx={{ paddingBottom: 1 }}>
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
      {isEditing && (
        <Grid item xs={12} sx={{ padding: 1 }}>
          <Divider sx={{ marginBottom: 1 }} />
        </Grid>
      )}
      <UserSelectModal
        modalOpen={showUserSelect}
        setModalOpen={setShowUserSelect}
        setUser={handleUserSelect}
        allowSetToMe={false}
        userObjectRet={true}
        chapter={localChapterId}
        spd={spdChapterId}
        activeMembers={true}
        statusFilter="active"
      />
      <ConfirmationDialog
        open={removeRecord}
        handleOk={handleConfirmRemove}
        handleCancel={() => {
          setRemoveRecord(false);
        }}
        title={'Remove Record'}
        message={`Are you sure you want to remove this record? This action cannot be undone and will remove this person from ever being in this position.`}
        confirmText={'Remove'}
        cancelText={'Cancel'}
      />
      <MessageCreate
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        id={userId}
        name={name}
        toType={'user'}
        screenName={pageFrom}
      />
      <Popover
        open={Boolean(roleHistoryAnchor)}
        anchorEl={roleHistoryAnchor}
        onClose={handleRoleHistoryClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
          {roleHistoryLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          )}
          {roleHistoryError && (
            <Typography variant="body2" color="error">
              {roleHistoryError}
            </Typography>
          )}
          {roleHistoryData && !roleHistoryLoading && (
            <>
              {roleHistoryData.future.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  Upcoming
                </Typography>
              )}
              {roleHistoryData.future.map((m, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {m.name} (starts {dayjs(m.start_date).format('M/D/YYYY')})
                </Typography>
              ))}
              {roleHistoryData.future.length > 0 &&
                roleHistoryData.past.length > 0 && (
                  <Box sx={{ mt: 1.5, mb: 0.5 }} />
                )}
              {roleHistoryData.past.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  Past (last 5 years)
                </Typography>
              )}
              {roleHistoryData.past.map((m, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                  {m.name}{' '}
                  {m.end_date
                    ? `(${dayjs(m.start_date).format('M/D/YYYY')} - ${dayjs(m.end_date).format('M/D/YYYY')})`
                    : `(${dayjs(m.start_date).format('M/D/YYYY')})`}
                </Typography>
              ))}
              {roleHistoryData.future.length === 0 &&
                roleHistoryData.past.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No additional history for this role.
                  </Typography>
                )}
            </>
          )}
        </Box>
      </Popover>
    </Grid>
  );
}
